import { LogOut, Settings, X } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { NavItem } from "@onemileauto/ui";
import type { MailboxMembership } from "@onemileauto/shared-types";
import { logout } from "../../lib/auth-client";
import { FolderNav } from "./folder-nav";
import { MailboxSwitcher } from "./mailbox-switcher";

async function handleLogout() {
  await logout();
  window.location.href = "/login";
}

interface FolderDrawerProps {
  mailboxId: string | undefined;
  activeSlug: string;
  onSelect: (slug: string) => void;
  onClose: () => void;
  /** Phase 5: mailbox switcher + admin entry point, mirroring the desktop rail (see mail-shell.tsx) — mobile's only path to either, since the rail itself is md:hidden here. */
  mailboxes: MailboxMembership[];
  isAggregate: boolean;
  onSelectMailbox: (mailboxIdOrAll: string) => void;
  isOwnerOfAny: boolean;
  /** Phase 6: owner/full-permission users only — see mail-shell.tsx. */
  canManageFolders?: boolean;
}

/**
 * Mobile-only folder drawer, triggered by the hamburger button in the main
 * pane's top bar (see mail-shell.tsx). Gmail's mobile web/app pattern: the
 * folder rail that's persistent on desktop collapses behind a
 * hamburger-triggered slide-over here, rather than disappearing — one tap
 * to open, one tap (a folder, the backdrop, or the × ) to close.
 */
export function FolderDrawer({
  mailboxId,
  activeSlug,
  onSelect,
  onClose,
  mailboxes,
  isAggregate,
  onSelectMailbox,
  isOwnerOfAny,
  canManageFolders,
}: FolderDrawerProps) {
  return (
    <div className="fixed inset-0 z-40 md:hidden">
      <button
        type="button"
        aria-label="Close folders"
        onClick={onClose}
        className="absolute inset-0 bg-charcoal/40"
      />
      <div className="absolute inset-y-0 left-0 flex w-[82%] max-w-xs flex-col bg-cream shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="font-display text-lg text-charcoal">Folders</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-10 w-10 items-center justify-center rounded-full text-muted hover:bg-parchment-dim hover:text-charcoal"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-3">
          <MailboxSwitcher
            mailboxes={mailboxes}
            currentMailboxId={mailboxId}
            isAggregate={isAggregate}
            onSelect={(id) => {
              onSelectMailbox(id);
              onClose();
            }}
          />
          {!isAggregate && (
            <FolderNav
              mailboxId={mailboxId}
              activeSlug={activeSlug}
              onSelect={(slug) => {
                onSelect(slug);
                onClose();
              }}
              canManageFolders={canManageFolders}
            />
          )}
          <div className="mt-auto border-t border-border pt-2">
            {isOwnerOfAny && (
              <Link to="/admin" onClick={onClose}>
                <NavItem icon={Settings} label="Manage mailboxes" />
              </Link>
            )}
            <NavItem icon={LogOut} label="Log out" onClick={handleLogout} />
          </div>
        </div>
      </div>
    </div>
  );
}
